import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheck, FaPlus, FaMinus } from 'react-icons/fa'

const ExchangesCustodians = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index)
  }


  const differentiationPoints = [
    {
      text: 'Pair independent CPA attestations with crypto-native Merkle Tree technology with',
      link: 'Merkle Tree Proof of Reserves.',
      url: '/services/merkle-tree-por'
    },
    {
      text: 'Demonstrate balance and ownership of digital assets held on-chain, with qualified custodians, or with counterparties.'
    },
    {
      text: 'Enable customers to verify inclusion with the Proof of Reserves by using Merkle Trees, the budding industry standard for exchanges and custodians.'
    }
  ]

  const growthItems = [
    {
      title: 'Increase Credibility by Providing Transparency to Account Holders, Counterparties, and More',
      content: 'Build confidence with current and prospective users with industry-leading transparency.'
    },
    {
      title: 'Fulfill Your Regulatory Requirements',
      content: 'Navigate compliance with stringent regulators like MICA, VARA, NYDFS, BMA, and more.'
    },
    {
      title: 'Reduce Costs with Streamlined Processes, Controls, and Operations',
      content: 'Streamline your operations with automation, outsourcing, and cost reduction.'
    },
    {
      title: 'Support Fundraising & Due Diligence Efforts',
      content: 'Strengthen your fundraising efforts and simplify due duligence with trasnparency, demonstrable controls, and strong financial reporting.'
    },
  ]

  const services = [
    {
      title: 'Merkle Tree Proof of Reserves',
      description: 'Demonstrate assets in custody are greater than customer liabilities via independent CPA attestation report. Enable users to verify for themselves via Merkle Tree.'
    },
    {
      title: 'Financial Statement Audit',
      description: 'Complete your audit to ensure your financial statements meet professional standards, enhancing stakeholder confidence and supporting compliance efforts. Ease the burden of completing your audit by working with a crypto-native team of CPAs who understand your business.'
    },
    {
      title: 'Internal Control Examinations',
      description: 'Complete your controls examination to secure licenses, meet regulatory requirements, and deliver transparency to investors. Simplify the process by partnering with a crypto-native team of CPAs who truly understand your business needs.'
    },
    {
      title: 'Outsourced Accounting & CFO',
      description: 'Streamline your financial operations with our crypto-native accounting and CFO services, providing expert insights, cost efficiency, and strategic guidance tailored to drive your business growth'
    },
    {
      title: 'Compliance Readiness Services',
      description: "Avoid suprises and streamline the completion of your financial audit, SOC 1, or SOC 2 licensing requirement by using The Audit Financier Team's experts to help guide you along the process."
    },
    {
      title: 'Business Process & Operations Advisory',
      description: 'Implement crypto friendly systems. Scale your operations with policies, procedures and SOPs. Reduce overhead through operational efficiencies.'
    },
  ]

  return (
    <>
      <Helmet>
        <title>Exchange & Custodian Services - Audit Financier</title>
        <meta name="description" content="Custom-tailored solutions for crypto exchanges, custodians, and banks. Differentiate through transparency and build trust." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section - Outsourced Accounting */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 md:py-32 overflow-hidden">
          {/* Animated Particle Wave Background */}
          <div className="absolute inset-0 opacity-30">
            <svg className="absolute bottom-0 w-full h-auto" viewBox="0 0 1280 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4A9BFF" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#4A9BFF" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#4A9BFF" stopOpacity="0.3" />
                </linearGradient>
              </defs>

              {/* Multiple wave layers for depth */}
              <path
                d="M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.4"
              >
                <animate
                  attributeName="d"
                  dur="8s"
                  repeatCount="indefinite"
                  values="
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,250 640,200 T1280,200 L1280,400 L0,400 Z;
                    M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
                />
              </path>

              <path
                d="M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                fill="url(#waveGradient)"
                opacity="0.3"
              >
                <animate
                  attributeName="d"
                  dur="10s"
                  repeatCount="indefinite"
                  values="
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,290 640,240 T1280,240 L1280,400 L0,400 Z;
                    M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                />
              </path>

              {/* Dotted particle effect */}
              <g opacity="0.6">
                {[...Array(50)].map((_, i) => {
                  const x = (i * 25.6) % 1280;
                  const y = 150 + (i * 13) % 200;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="2"
                      fill="#4A9BFF"
                      opacity="0.5"
                    >
                      <animate
                        attributeName="opacity"
                        values="0.3;0.8;0.3"
                        dur={`${3 + (i % 4)}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="cy"
                        values={`${y};${y - 20};${y}`}
                        dur={`${4 + (i % 3)}s`}
                        repeatCount="indefinite"
                      />
                    </circle>
                  )
                })}
              </g>
            </svg>
          </div>
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Exchange & Custodian
                <br />
                <span className="text-accent">Outsourced Accounting</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl">
                Custom-tailored solutions for crypto exchanges, custodians, and banks.
              </p>
              
              <div className="space-y-4 mb-10">
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0 text-xl" />
                  <p className="text-lg md:text-xl">
                    Differentiate from Competitors by using Transparency as a Competitive Advantage
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0 text-xl" />
                  <p className="text-lg md:text-xl">
                    Optimize Operations, Bolster Processes, and Fulfill Compliance Requirements
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheck className="text-accent mt-1 flex-shrink-0 text-xl" />
                  <p className="text-lg md:text-xl">
                    Establish Credibility with Counterparties across DeFi, CeFi, and TradFi
                  </p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-md text-base md:text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>


        {/* Differentiate Section */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Column - Title and Icon */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-accent mb-8 leading-tight">
                  Differentiate by using <span className="underline decoration-accent">Transparency as a Competitive Advantage</span>
                </h2>
                
                {/* Merkle Tree Icon */}
                <div className="flex justify-center md:justify-start mt-8">
                  <svg className="w-48 h-48 text-accent" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Root Node */}
                    <rect x="75" y="10" width="50" height="50" fill="currentColor" />
                    
                    {/* Middle Layer */}
                    <rect x="30" y="85" width="50" height="50" fill="currentColor" />
                    <rect x="120" y="85" width="50" height="50" fill="currentColor" />
                    
                    {/* Bottom Layer */}
                    <rect x="5" y="160" width="50" height="50" fill="currentColor" />
                    <rect x="55" y="160" width="50" height="50" fill="currentColor" />
                    <rect x="95" y="160" width="50" height="50" fill="currentColor" />
                    <rect x="145" y="160" width="50" height="50" fill="currentColor" />
                    
                    {/* Connecting Lines */}
                    <line x1="100" y1="60" x2="55" y2="85" stroke="currentColor" strokeWidth="3" />
                    <line x1="100" y1="60" x2="145" y2="85" stroke="currentColor" strokeWidth="3" />
                    
                    <line x1="55" y1="135" x2="30" y2="160" stroke="currentColor" strokeWidth="3" />
                    <line x1="55" y1="135" x2="80" y2="160" stroke="currentColor" strokeWidth="3" />
                    
                    <line x1="145" y1="135" x2="120" y2="160" stroke="currentColor" strokeWidth="3" />
                    <line x1="145" y1="135" x2="170" y2="160" stroke="currentColor" strokeWidth="3" />
                  </svg>
                </div>
              </div>

              {/* Right Column - Bullet Points */}
              <div className="space-y-6">
                {differentiationPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <FaCheck className="text-accent mt-1 flex-shrink-0 text-xl" />
                    <p className="text-lg text-gray-700">
                      {point.text}
                      {point.link && (
                        <>
                          {' '}
                          <Link to={point.url || '#'} className="text-accent hover:underline font-semibold">
                            {point.link}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Unlock Your Next Stage of Growth - Accordion */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-accent mb-12">
              Unlock Your Next Stage of Growth
            </h2>
            
            <div className="max-w-5xl mx-auto">
              <div className="border-t border-gray-200">
                {growthItems.map((item, index) => (
                  <div key={index} className="border-b border-gray-200">
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition-colors group"
                    >
                      <span className="text-lg md:text-xl font-semibold text-gray-900 pr-4">
                        {item.title}
                      </span>
                      <div className="flex-shrink-0">
                        {openAccordion === index ? (
                          <FaMinus className="text-accent text-xl" />
                        ) : (
                          <FaPlus className="text-accent text-xl" />
                        )}
                      </div>
                    </button>
                    
                    <div className={`overflow-hidden transition-all duration-300 ${
                      openAccordion === index ? 'max-h-96' : 'max-h-0'
                    }`}>
                      <div className="px-6 md:px-8 pb-6 md:pb-8 text-gray-700 text-base md:text-lg">
                        <p>{item.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Tailored Section */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-accent mb-12 md:mb-16">
              Services Tailored for Exchanges & Custodians
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-lg hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-xl font-bold mb-4 text-primary">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/contact"
                className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-md text-base md:text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                TALK TO AN EXPERT
              </Link>
            </div>
          </div>
        </section>

        {/* Bottom Image Section (placeholder for future image) */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
                {/* Placeholder for bottom image - will be replaced with actual image */}
                <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold p-12">
                  <p className="text-center">Ready to elevate your platform with transparency?</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ExchangesCustodians